// PDF underlay support for the Room Takeoff canvas. We store the original PDF
// (compact vector data) and rasterize it live at the current zoom, so the
// drawing stays crisp at any magnification instead of being flattened to a
// low-res JPEG on upload. pdfjs is loaded lazily so it never touches the main
// bundle for users who don't open a drawing.
//
// Coordinate model: PDF pages are measured in points (1/72 inch). The canvas
// places the page in WORLD FEET using the drawing scale (feet per paper inch),
// so re-rendering at a different pixel resolution never moves anything.

import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { isBlobPointer, blobNameOfPointer, getStorageToken, headBlob, fetchBlobRange } from "./blobStorage";

let _pdfjs = null;
async function lib() {
  if (!_pdfjs) {
    _pdfjs = await import("pdfjs-dist");
    _pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
  }
  return _pdfjs;
}

export const isPdfDataUrl = (s) => typeof s === "string" && s.startsWith("data:application/pdf");
// A PDF the viewer can open: an inline data URL, or a pointer to a PDF stored in the drawings container.
export const isPdfSource = (s) => isPdfDataUrl(s) || isBlobPointer(s);

// ── PDFs stored in the drawings container ───────────────────────────────────────
// One pdf.js document per stored file, shared by every sheet that is a page of it (a 100-sheet set is ONE document),
// read with HTTP range requests so only the pages that are opened are downloaded. The transport asks for a fresh
// token on every range, so a drawing left open past a token's lifetime keeps working.
const BLOB_DOCS = new Map(); // blobName -> Promise<pdf.js document>
async function openBlobDoc(name) {
  if (BLOB_DOCS.has(name)) return BLOB_DOCS.get(name);
  const p = (async () => {
    const pdfjs = await lib();
    const token = await getStorageToken();
    if (!token) throw new Error("Sign-in for drawing storage is not available — sign in again, or ask an administrator to check Setup ▸ Integrations ▸ Test drawing storage.");
    const { exists, bytes } = await headBlob(name, { token });
    if (!exists || !bytes) throw new Error("The stored drawing set could not be found.");
    const transport = new pdfjs.PDFDataRangeTransport(bytes, null);
    transport.requestDataRange = (begin, end) => {
      (async () => {
        const chunk = await fetchBlobRange(name, begin, Math.min(end, bytes), { token: await getStorageToken() });
        transport.onDataRange(begin, chunk);
      })().catch(() => { /* pdf.js times the request out and reports the page as failed */ });
    };
    const doc = await pdfjs.getDocument({ range: transport, length: bytes, disableAutoFetch: true, disableStream: true }).promise;
    doc.__shared = true;   // never destroyed by a single caller
    return doc;
  })();
  BLOB_DOCS.set(name, p);
  p.catch(() => BLOB_DOCS.delete(name));   // a failure is not cached
  return p;
}

// Drawing rotation (Feedback #29): the estimator may turn a sheet 0 / 90 / 180 / 270 degrees clockwise. pdf.js's
// `rotation` REPLACES the page's own /Rotate, so the viewport gets (page /Rotate + the drawing's rotation). With no
// extra rotation the options are exactly what they always were.
export const normalizeRotation = (deg) => { const n = (((Math.round(+deg || 0) % 360) + 360) % 360); return n - (n % 90); };
export function viewportOf(page, scale, rotation = 0) {
  const r = normalizeRotation(rotation);
  return r ? page.getViewport({ scale, rotation: ((page.rotate || 0) + r) % 360 }) : page.getViewport({ scale });
}

// Load a PDF from a data URL, or from a pointer to a stored set (opts.page = the one page this sheet shows).
// Returns { numPages, pageSizes: [{wPt,hPt}], doc }. For a stored set only the requested page's size is read (the
// other entries are null) — measuring every page of a 100-page set would download most of the file.
// Page count only (no page is measured or rendered) — for numbering pages across several drawings.
export async function countPdfPages(dataUrl) {
  const pdfjs = await lib();
  const bytes = Uint8Array.from(atob(dataUrl.split(",")[1] || ""), c => c.charCodeAt(0));
  const doc = await pdfjs.getDocument({ data: bytes }).promise;
  const n = doc.numPages;
  try { await doc.destroy(); } catch { /* the count is what matters */ }
  return n;
}

export async function loadPdf(dataUrl, { page = 1, rotation = 0 } = {}) {
  if (isBlobPointer(dataUrl)) {
    const doc = await openBlobDoc(blobNameOfPointer(dataUrl));
    const p = Math.min(Math.max(1, page || 1), doc.numPages);
    const vp = viewportOf(await doc.getPage(p), 1, rotation);
    const pageSizes = Array.from({ length: doc.numPages }, () => null);
    pageSizes[p - 1] = { wPt: vp.width, hPt: vp.height };
    return { numPages: doc.numPages, pageSizes, doc };
  }
  const pdfjs = await lib();
  const base64 = dataUrl.split(",")[1] || "";
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const doc = await pdfjs.getDocument({ data: bytes }).promise;
  const pageSizes = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const vp = viewportOf(page, 1, rotation); // width/height in points, drawing rotation applied
    pageSizes.push({ wPt: vp.width, hPt: vp.height });
  }
  return { numPages: doc.numPages, pageSizes, doc };
}

// Rasterize only a sub-region of a page (crop in PDF points) at a given scale
// (device px per point). Rendering just the visible window at screen resolution
// keeps the underlay crisp at any zoom — like a PDF viewer — instead of scaling
// one fixed full-page texture. Returns a PNG data URL.
export async function renderPageRegion(doc, pageNum, scale, cropXpt, cropYpt, cropWpt, cropHpt, rotation = 0) {
  const page = await doc.getPage(pageNum);
  const vp = viewportOf(page, scale, rotation);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cropWpt * scale));
  canvas.height = Math.max(1, Math.round(cropHpt * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // Shift the page so the crop's top-left lands at the canvas origin.
  await page.render({
    canvasContext: ctx,
    viewport: vp,
    transform: [1, 0, 0, 1, -cropXpt * scale, -cropYpt * scale],
  }).promise;
  return canvas.toDataURL("image/png");
}

// A3 (2026-09-14) — a whole page as a small PNG (longest side = maxPx) for the
// Sheets dock thumbnails; cached per sheet by the caller for the session.
export async function renderPageThumbnail(doc, pageNum, maxPx = 220, rotation = 0) {
  const page = await doc.getPage(pageNum);
  const vp1 = viewportOf(page, 1, rotation);
  const scale = maxPx / Math.max(vp1.width, vp1.height);
  return renderPageRegion(doc, pageNum, scale, 0, 0, vp1.width, vp1.height, rotation);
}
// A3 — a whole page at reading resolution for the side-by-side compare pane.
export const renderPageFull = (doc, pageNum, maxPx = 2400, rotation = 0) => renderPageThumbnail(doc, pageNum, maxPx, rotation);

// A raster drawing turned clockwise by 90 / 180 / 270 (thumbnails and the compare pane; the main canvas rotates its
// <image> with an SVG transform instead). Returns the same URL for 0.
export function rotateImageUrl(url, rotation = 0) {
  const r = normalizeRotation(rotation);
  if (!r || !url) return Promise.resolve(url);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth, h = img.naturalHeight;
      const c = document.createElement("canvas");
      c.width = r === 180 ? w : h; c.height = r === 180 ? h : w;
      const ctx = c.getContext("2d");
      ctx.translate(c.width / 2, c.height / 2); ctx.rotate((r * Math.PI) / 180); ctx.drawImage(img, -w / 2, -h / 2);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Could not read the image"));
    img.src = url;
  });
}

/** Open a stored set for the page gallery: { doc, numPages }. */
export async function openStoredSet(blobName) {
  const doc = await openBlobDoc(blobName);
  return { doc, numPages: doc.numPages };
}

// Read an uploaded PDF File into a data URL (kept as-is; no rasterization).
export function pdfFileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error("Could not read PDF"));
    fr.readAsDataURL(file);
  });
}

// Standard US architectural scales. ftPerInch = real feet represented by one
// inch of paper (e.g. 1/4" = 1'-0"  ->  1 in paper = 4 ft  ->  ftPerInch = 4).
export const ARCH_SCALES = [
  { label: '1/32" = 1\'-0"', ftPerInch: 32 },
  { label: '1/16" = 1\'-0"', ftPerInch: 16 },
  { label: '3/32" = 1\'-0"', ftPerInch: 32 / 3 },
  { label: '1/8" = 1\'-0"',  ftPerInch: 8 },
  { label: '3/16" = 1\'-0"', ftPerInch: 16 / 3 },
  { label: '1/4" = 1\'-0"',  ftPerInch: 4 },
  { label: '3/8" = 1\'-0"',  ftPerInch: 8 / 3 },
  { label: '1/2" = 1\'-0"',  ftPerInch: 2 },
  { label: '3/4" = 1\'-0"',  ftPerInch: 4 / 3 },
  { label: '1" = 1\'-0"',    ftPerInch: 1 },
  { label: '1-1/2" = 1\'-0"', ftPerInch: 2 / 3 },
  { label: '3" = 1\'-0"',    ftPerInch: 1 / 3 },
];

// Nearest standard scale to a measured ftPerInch, for validation readouts.
export function nearestArchScale(ftPerInch) {
  if (!(ftPerInch > 0)) return null;
  let best = ARCH_SCALES[0], bestErr = Infinity;
  for (const s of ARCH_SCALES) {
    const err = Math.abs(s.ftPerInch - ftPerInch) / s.ftPerInch;
    if (err < bestErr) { bestErr = err; best = s; }
  }
  return { scale: best, errPct: bestErr * 100 };
}

// The text layer of one page as positioned items plus the page size, in VIEWPORT
// points (page /Rotate applied, scale 1) with the origin at the BOTTOM-LEFT:
// x grows right, y grows up. Input for scaleDetect.detectScaleNotes and
// planSearch. Scanned / flattened sheets have no text layer and return no items.
export async function extractPageText(doc, pageNum, rotation = 0) {
  const page = await doc.getPage(pageNum);
  const vp = viewportOf(page, 1, rotation);
  const tc = await page.getTextContent();
  const items = [];
  for (const i of tc.items) {
    if (typeof i.str !== "string" || !i.str.trim() || !Array.isArray(i.transform)) continue;
    // transform[4], [5] are in unrotated PDF user space; convert so a rotated sheet maps correctly
    const [vx, vy] = vp.convertToViewportPoint(i.transform[4], i.transform[5]);
    items.push({ str: i.str, x: vx, y: vp.height - vy, w: Number.isFinite(i.width) ? i.width : 0 });
  }
  return { items, page: { width: vp.width, height: vp.height } };
}
